/// <reference types="vite-plugin-svgr/client" />
import { GlobalMessages } from '@shared/localization/messages';
import _sum from 'lodash/sum';
import { useCallback, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useConditionalEffect } from '@/api/hooks/useConditionalEffect';
import DisketteIcon from '@/assets/diskette-icon.svg?react';
import { Button } from '@/components/atoms/Button';
import { ModalStateless } from '@/components/ModalStateless';
import { CustomEstimationItemCreationModal } from '@/components/molecules/CustomEstimationItemCreationModal';
import Search from '@/components/molecules/Search';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { useMessages } from '@/hooks/messages';
import { useControlledFieldArray } from '@/hooks/react-hook-form/useControlledFieldArray';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import type { OmitSafely } from '@/types';
import { generateTemporaryId, isTemporaryId } from '@/utils/generators';
import { sortExpressions } from '@/utils/sorting';

import { DeleteEstimationItemModal } from '../DeleteEstimationItemModal';
import { EstimationItemEditation } from '../EstimationItemEditation';
import type { EstimationItemsGroupedTableProps } from '../EstimationItemsGroupedTables';
import EstimationItemsGroupedTables from '../EstimationItemsGroupedTables';
import { mapFromResourceEstimationItemsTableRecord } from '../mappers/estimationItemsEditFormModelMapper';
import type {
  EstimationGroupActionCallback,
  EstimationItemActionCallback,
  EstimationItemDetail,
  EstimationItemEditModel,
  EstimationItemIsIncludedActionPromiseCallback,
  EstimationItemsEditFormModel,
  EstimationItemsFormModelRow,
  RecalculateCallback,
} from '../model';
import { OmitModal } from '../OmitModal';
import { estimationItemDetailModalMessageDefinitions } from './messages';

type EstimateItemDetailModalProps = OmitSafely<
  EstimationItemsGroupedTableProps,
  | 'control'
  | 'onIncludeClicked'
  | 'calculatedResultByEstimationItemId'
  | 'searchText'
  | 'formModelEstimationItems'
> & {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    formModel: EstimationItemsEditFormModel,
    removeEstimationItemFromModelById: EstimationItemActionCallback,
  ) => Promise<void>;
  shouldResetForm: boolean;
  onFormReset: () => void;
  estimationItem: EstimationItemDetail | undefined;
  estimationMethodologyId: string;
  estimationCultureName: string;
  onAssumptionsClicked: EstimationItemActionCallback;
  onCommentsClicked: EstimationItemActionCallback;
  recalculate?: RecalculateCallback;
  exportProps: OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>;
};

const EstimationItemDetailModal: React.FC<EstimateItemDetailModalProps> = ({
  open,
  onClose,
  onSubmit,
  shouldResetForm,
  onFormReset,
  estimationItem,
  estimationItems,
  estimationMethodologyId,
  estimationCultureName,
  onDetailsClicked,
  onAssumptionsClicked,
  onCommentsClicked,
  isReadOnly,
  methodologies,
  estimationModel,
  recalculate,
  exportProps,
  onItemRename,
  ...remainingTableProps
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(estimationItemDetailModalMessageDefinitions);
  const [searchText, setSearchText] = useState('');
  const [estimationItemIdForOmitModal, setEstimationItemIdForOmitModal] = useState<string>();
  const [estimationItemIdForDeleteModal, setEstimationItemIdForDeleteModal] = useState<string>();
  const [
    estimationGroupIdForCustomEstimationItemCreationModal,
    setEstimationGroupIdForCustomEstimationItemCreationModal,
  ] = useState<string | null>();
  const [isUnsavedChangesWarningOpen, setIsUnsavedChangesWarningOpen] = useState(false);

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => {
      if (estimationItem) {
        void estimationExporter(format, estimationItem.id);
      }
    },
    [estimationExporter, estimationItem],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const formModelInitial = useMemo(() => {
    if (!estimationItem) {
      return undefined;
    }
    const mainEstimationItem: EstimationItemEditModel = {
      id: estimationItem.id,
      name: estimationItem.name,
      description: estimationItem.description,
      phaseLeadId: estimationItem.phaseLeadId,
      methodologyId: estimationItem.hasEstimationModelItemParameters
        ? undefined
        : (estimationItem.methodologyId ?? estimationMethodologyId),
      isParentBasedCalculation: estimationItem.isParentBasedCalculation,
    };
    const subItems = estimationItems.map((x) =>
      mapFromResourceEstimationItemsTableRecord(x, estimationMethodologyId, estimationModel),
    );
    return {
      mainEstimationItem,
      estimationItems: subItems,
    };
  }, [estimationItem, estimationItems, estimationMethodologyId, estimationModel]);

  const { reset, watch, setValue, getValues, handleSubmit, control, formState } =
    useForm<EstimationItemsEditFormModel>({ defaultValues: formModelInitial });
  const { append, remove, fields } = useControlledFieldArray({ control, name: 'estimationItems' });

  const resetDirtyFieldsState = useCallback(() => {
    reset(undefined, { keepValues: true }); // Retain current values but clear dirty state
  }, [reset]);

  const removeEstimationItemFromModelById = useCallback<EstimationItemActionCallback>(
    (estimationItemId: string) => {
      /**
       * Workaround:
       * There's an issue when removing item from collection when last item has a field with dirty state and form reset with keepDirtyValues:true is called.
       * It then tries to keep value of an item that is no longer maintained in model. As a result, it appends some inconsistent partial field at the end of collection.
       * In order to prevent it from happening, when removing item we firstly simply reset dirty fields state so that when fresh data are fetched after form submit, all the fields are reset (because there's no dirty one anymore).
       */
      resetDirtyFieldsState();
      return remove(fields.findIndex((field) => field.id === estimationItemId));
    },
    [remove, fields, resetDirtyFieldsState],
  );

  const formModelMainEstimationItem = watch('mainEstimationItem');

  const calculatedResultByEstimationItemId = useMemo(
    () =>
      fields && recalculate
        ? recalculate(fields)
        : new Map<string, number>(
            estimationItems.map((item) => [item.id, item.calculatedResult ?? 0]),
          ),
    [fields, recalculate, estimationItems],
  );

  const enrichedOnSubmit = useCallback(
    async (formModel: EstimationItemsEditFormModel) => {
      await onSubmit(formModel, removeEstimationItemFromModelById);
    },
    [onSubmit, removeEstimationItemFromModelById],
  );

  // EWM-883: the ✗ icon, Escape and a backdrop click all funnel through ModalStateless's single
  // `onClose`, so guarding that one callback covers every accidental-close path. Only the explicit
  // footer buttons ("Schätzung aktualisieren" / "Abbrechen") bypass the guard.
  // Mantine registers Escape on `window` per modal (ModalBase/use-modal), so a nested modal's
  // Escape reaches this one too - bail out while any nested modal owns the interaction.
  const hasNestedModalOpen =
    isUnsavedChangesWarningOpen ||
    !!estimationItemIdForOmitModal ||
    !!estimationItemIdForDeleteModal ||
    estimationGroupIdForCustomEstimationItemCreationModal !== undefined;

  /**
   * Mantine wires Escape to a window-level capture listener per modal, and React flushes this
   * discrete event synchronously - so a warning mounted *during* the Escape dispatch gets its own
   * listener attached in time to receive that very same keypress and close itself again. The user
   * saw Escape do nothing at all. Opening on the next macrotask lets the keydown finish first.
   */
  const openUnsavedChangesWarning = useCallback(() => {
    setTimeout(() => setIsUnsavedChangesWarningOpen(true), 0);
  }, []);

  const handleCloseRequest = useCallback(() => {
    if (hasNestedModalOpen) {
      return;
    }
    // Inline renames deliberately write with `shouldDirty: false` (EWM-589), so a name-only edit
    // leaves the form clean and closes without a prompt.
    if (formState.isDirty) {
      openUnsavedChangesWarning();
      return;
    }
    onClose();
  }, [hasNestedModalOpen, formState.isDirty, onClose, openUnsavedChangesWarning]);

  // Same path as "Schätzung aktualisieren": validation runs first, and the modal only closes once
  // the mutation has resolved.
  const handleSaveAndClose = handleSubmit(async (formModel) => {
    await enrichedOnSubmit(formModel);
    setIsUnsavedChangesWarningOpen(false);
    onClose();
  });

  // Inline rename for the main item: hits the lightweight PATCH endpoint and keeps the form
  // value in sync so a subsequent "Schätzung aktualisieren" doesn't overwrite the new name.
  const handleMainItemRename = useCallback(
    async (newName: string) => {
      if (!estimationItem || !onItemRename) return;
      await onItemRename(estimationItem.id, newName);
      setValue('mainEstimationItem.name', newName, { shouldDirty: false });
    },
    [estimationItem, onItemRename, setValue],
  );

  // Inline rename for a sub-item ("Sub-Stream"): the sub-items table renders the name from this
  // modal's own form model (fields), which is only reset when shouldResetForm fires. Writing the
  // new name straight into the form model makes the renamed row update right after the tick,
  // instead of only after the next full "Update Estimate" submit. The mutation still persists +
  // invalidates the query for consistency. EWM-739.
  const handleSubItemRename = useCallback(
    async (estimationItemId: string, newName: string) => {
      if (!onItemRename) return;
      await onItemRename(estimationItemId, newName);
      const index = fields.findIndex((field) => field.id === estimationItemId);
      if (index >= 0) {
        setValue(`estimationItems.${index}.name`, newName, { shouldDirty: false });
      }
    },
    [onItemRename, fields, setValue],
  );

  useConditionalEffect(
    () => {
      reset(formModelInitial);
      onFormReset();
    },
    [formModelInitial, reset, onFormReset],
    shouldResetForm,
  );

  const subItemsRealTimeSum = useMemo(() => {
    if (!fields || fields.length === 0) {
      return undefined;
    }
    return _sum(
      fields.filter((x) => x.isIncluded).map((x) => calculatedResultByEstimationItemId.get(x.id)),
    );
  }, [fields, calculatedResultByEstimationItemId]);

  const onMethodologyChanged = useCallback(
    async (_estimationItemId: string, methodologyId: string | null) => {
      const newMethodology = methodologies.find((x) => x.id === methodologyId);
      if (!newMethodology) {
        throw new Error('Methodology not found');
      }
      const newParameters = newMethodology.defaultParameters.map((x) => ({
        parameterName: x.label,
        value: 0,
      }));

      fields.forEach((_, formModelIndex) => {
        setValue(`estimationItems.${formModelIndex}.methodologyId`, methodologyId);
        setValue(`estimationItems.${formModelIndex}.parameters`, newParameters);
      });
      return Promise.resolve(true);
    },
    [setValue, fields, methodologies],
  );

  const onIncludeClicked: EstimationItemIsIncludedActionPromiseCallback = (
    estimationItemId,
    isIncluded,
  ) => {
    // excluding or deleting
    if (isTemporaryId(estimationItemId)) {
      // if it's just temporarily added item but not saved yet => remove it straight away
      const formModelItemIndex = fields.findIndex((x) => x.id === estimationItemId);
      remove(formModelItemIndex);
      setValue('estimationItems', getValues('estimationItems'));
      return Promise.reject(new Error('Item removed'));
    }
    if (!isIncluded) {
      const estimItem = estimationItems.find((x) => x.id === estimationItemId);
      if (!estimItem) {
        throw new Error('Estimation item not found.');
      }
      // custom estimation item => display delete confirmation modal
      if (!estimItem.estimationModelItemId) {
        setEstimationItemIdForDeleteModal(estimationItemId);
        return Promise.reject(new Error('Postponing change of the value after confirmation'));
      }
      // required estimation model item => display omit reason modal
      if (estimItem.isRequired) {
        setEstimationItemIdForOmitModal(estimationItemId);
        return Promise.reject(new Error('Postponing change of the value after confirmation'));
      }
      // omit straight away
      setOmit(fields, estimationItemId, setValue, null);
      return Promise.resolve();
    }
    // including
    const rowIndex = fields.findIndex((x) => x.id === estimationItemId);
    setValue(`estimationItems.${rowIndex}.omitReason`, null);
    return Promise.resolve();
  };

  const onAddPhaseClicked: EstimationGroupActionCallback = (estimationGroupId) => {
    return setEstimationGroupIdForCustomEstimationItemCreationModal(
      estimationGroupId ?? estimationModel.itemGroups[0].id,
    );
  };

  return (
    <ModalStateless
      title={messages.title()}
      open={open}
      onClose={handleCloseRequest}
      withoutButtons
    >
      {estimationItem && formModelMainEstimationItem && calculatedResultByEstimationItemId && (
        <>
          <form onSubmit={handleSubmit(enrichedOnSubmit)}>
            <EstimationItemEditation
              control={control}
              estimationItem={estimationItem}
              methodologies={methodologies}
              isReadOnly={isReadOnly}
              subItemsRealTimeSum={subItemsRealTimeSum}
              onMethodologyChange={onMethodologyChanged}
              onInlineRename={onItemRename ? handleMainItemRename : undefined}
            />
            <div className='my-4 flex grid grid-cols-2 gap-4 sm:grid-cols-4'>
              <Button
                className='flex-1'
                variant='secondary'
                outlined
                onClick={() => onAssumptionsClicked(estimationItem.id)}
              >
                {messages.assumptionsButton()}
              </Button>
              <Button
                className='flex-1'
                variant='secondary'
                outlined
                onClick={() => onCommentsClicked(estimationItem.id)}
              >
                {messages.commentsButton()}
              </Button>
              <div className='hidden sm:block' />
              {isReadOnly ? (
                <div className='hidden sm:block' />
              ) : (
                <Button
                  className='flex-1'
                  type='submit'
                  variant='warning'
                  IconComponentLeft={DisketteIcon}
                >
                  {messages.overrideOriginalEstimateButton()}
                </Button>
              )}
            </div>
            <hr className='my-8' />
            <h2>{messages.subItemsTitle()}</h2>
            <div className='mt-4 flex justify-end gap-2 rounded-xl'>
              <Search handleOnChange={setSearchText} />
              <ExportButtonsSelector
                exportConfiguration={exportConfiguration}
                onExportButtonClick={handleExportButtonClick}
              />
            </div>
            <EstimationItemsGroupedTables
              control={control}
              formModelEstimationItems={fields}
              estimationItems={estimationItems}
              methodologies={methodologies}
              isReadOnly={isReadOnly}
              onAssumptionsClicked={onAssumptionsClicked}
              onCommentsClicked={onCommentsClicked}
              onDetailsClicked={onDetailsClicked}
              onIncludeClicked={onIncludeClicked}
              estimationModel={estimationModel}
              calculatedResultByEstimationItemId={calculatedResultByEstimationItemId}
              onAddPhaseClicked={onAddPhaseClicked}
              searchText={searchText}
              onItemRename={onItemRename ? handleSubItemRename : undefined}
              {...remainingTableProps}
            />
            <div className='my-4 flex justify-end space-x-4'>
              <Button variant='secondary' outlined onClick={onClose}>
                {globalMessages.cancel()}
              </Button>
              {!isReadOnly && (
                <Button
                  data-testid='phase-detail-update-button'
                  type='submit'
                  variant='warning'
                  IconComponentLeft={DisketteIcon}
                >
                  {messages.overrideOriginalEstimateButton()}
                </Button>
              )}
            </div>
          </form>
          <ModalStateless
            title={messages.unsavedChangesTitle()}
            size='s'
            open={isUnsavedChangesWarningOpen}
            onClose={() => setIsUnsavedChangesWarningOpen(false)}
            data-testid='phase-detail-unsaved-changes-modal'
            buttons={
              <>
                {/* `data-autofocus` is Mantine's focus-trap hook: it makes this the default
                    focus, which also makes Enter activate it. */}
                <Button
                  data-autofocus
                  data-testid='phase-detail-unsaved-save-and-close'
                  variant='warning'
                  IconComponentLeft={DisketteIcon}
                  onClick={() => void handleSaveAndClose()}
                >
                  {globalMessages.saveAndClose()}
                </Button>
                <Button
                  data-testid='phase-detail-unsaved-discard-and-close'
                  variant='danger'
                  outlined
                  onClick={() => {
                    setIsUnsavedChangesWarningOpen(false);
                    onClose();
                  }}
                >
                  {messages.discardAndCloseButton()}
                </Button>
                <Button
                  data-testid='phase-detail-unsaved-cancel'
                  variant='secondary'
                  outlined
                  onClick={() => setIsUnsavedChangesWarningOpen(false)}
                >
                  {globalMessages.cancel()}
                </Button>
              </>
            }
          >
            <p>{messages.unsavedChangesWarning()}</p>
          </ModalStateless>
          <OmitModal
            open={!!estimationItemIdForOmitModal}
            onClose={() => {
              setEstimationItemIdForOmitModal(undefined);
            }}
            onConfirm={(reason) => {
              setOmit(fields, estimationItemIdForOmitModal, setValue, reason);
              const rowIndex = fields.findIndex((x) => x.id === estimationItemIdForOmitModal);
              setValue(`estimationItems.${rowIndex}.omitReason`, reason, { shouldDirty: true });
              setValue(`estimationItems.${rowIndex}.isIncluded`, false, { shouldDirty: true });
              setEstimationItemIdForOmitModal(undefined);
            }}
          />
          <DeleteEstimationItemModal
            open={!!estimationItemIdForDeleteModal}
            onClose={() => {
              setEstimationItemIdForDeleteModal(undefined);
            }}
            onConfirm={() => {
              setDelete(fields, estimationItemIdForDeleteModal, setValue);
              setEstimationItemIdForDeleteModal(undefined);
            }}
          />
          <CustomEstimationItemCreationModal
            estimationModel={estimationModel}
            defaultMethodologyId={
              formModelMainEstimationItem.methodologyId ?? estimationMethodologyId
            }
            canChangeMethodology={false}
            defaultGroupId={estimationGroupIdForCustomEstimationItemCreationModal ?? undefined}
            estimationCultureName={estimationCultureName}
            open={estimationGroupIdForCustomEstimationItemCreationModal !== undefined}
            onClose={() => setEstimationGroupIdForCustomEstimationItemCreationModal(undefined)}
            onSave={(customEstimationItemCreationFormModel) => {
              if (!customEstimationItemCreationFormModel.groupId) {
                throw new Error('Group must be chosen');
              }
              const methodology = methodologies.find(
                (x) => x.id === customEstimationItemCreationFormModel.methodologyId,
              );
              if (!methodology) {
                throw new Error('Methodology must be chosen');
              }
              append({
                id: generateTemporaryId(),
                name: customEstimationItemCreationFormModel.name,
                description: customEstimationItemCreationFormModel.description,
                isIncluded: true,
                parentId: formModelMainEstimationItem.id,
                methodologyId: customEstimationItemCreationFormModel.methodologyId,
                groupId: customEstimationItemCreationFormModel.groupId,
                phaseLeadId: customEstimationItemCreationFormModel.phaseLeadId,
                omitReason: null,
                shouldDelete: false,
                resources: undefined,
                parameters: methodology.defaultParameters
                  .sort(sortExpressions.byOrder)
                  .map((methodologyParameter) => ({
                    parameterName: methodologyParameter.label,
                    value: null,
                  })),
              });
              setEstimationGroupIdForCustomEstimationItemCreationModal(undefined);
            }}
          />
        </>
      )}
    </ModalStateless>
  );
};

function setOmit(
  formModelEstimationItems: EstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<EstimationItemsEditFormModel>,
  reason: string | null,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.omitReason`, reason, { shouldDirty: true });
  setValue(`estimationItems.${rowIndex}.isIncluded`, false, { shouldDirty: true });
}

function setDelete(
  formModelEstimationItems: EstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<EstimationItemsEditFormModel>,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.shouldDelete`, true, { shouldDirty: true });
  setValue(`estimationItems.${rowIndex}.isIncluded`, false, { shouldDirty: true });
}

export default EstimationItemDetailModal;
